import { Alert, Box, Button, Card, CardContent, Chip, Divider, FormControl, FormControlLabel, Grid, IconButton, Radio, RadioGroup, styled, Switch, Typography, alpha } from "@mui/material"
import { FC, useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import AppModal from "components/AppModal"
import { H2, H4, H6 } from "components/Typography"
import FlexBox from "components/flexbox/FlexBox"
import CalendarInput from "components/input-fields/CalendarInput"
import { endOfWeek, startOfWeek, format, startOfMonth, endOfMonth, addDays, getDay, startOfDay, endOfDay, getISOWeek } from "date-fns"
import { shiftApi } from "api"
import { CopyShiftPlanDto, RepeatShiftPlanDto } from "api/generated"
import toast from "react-hot-toast"
import { ContentCopy, Warning, CalendarMonth, Schedule, DeleteForever, ArrowForward } from "@mui/icons-material"

interface ModalProps {
    open: boolean
    onClose: () => void
    departmentId: number
    seasonId?: number
    isDepchief?: boolean
    // Ids of the employees currently visible on the shift assignment page. Calendar
    // events are only copied for these employees, never for the whole department.
    employeeIds?: number[]
    fetchData: () => void
}

const StyledAppModal = styled(AppModal)(({ theme }) => ({
    maxWidth: 900,
    minWidth: 700,
    maxHeight: "90vh",
    overflow: "auto",
    [theme.breakpoints.down(750)]: { maxWidth: "100%", minWidth: "100%" },
}))

const QuickActionCard = styled(Card)(({ theme }) => ({
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
    border: `2px solid ${theme.palette.divider}`,
    "&:hover": {
        borderColor: theme.palette.primary.main,
        transform: "translateY(-2px)",
        boxShadow: theme.shadows[8],
    },
}))

const BetaChip = styled(Chip)(({ theme }) => ({
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    fontWeight: "bold",
    fontSize: "0.75rem",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
    "& .MuiChip-label": {
        padding: "4px 12px",
    },
}))

const CalendarDay = styled(Box)<{ color?: string }>(({ theme, color }) => ({
    width: "40px",
    height: "40px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: color || theme.palette.background.paper,
    position: "relative",
    "& .day-label": {
        fontSize: "0.75rem",
        fontWeight: "bold",
        color: color ? theme.palette.getContrastText(color) : theme.palette.text.primary,
    },
}))

const SelectionCard = styled(Card)(({ theme }) => ({
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    border: `2px solid ${theme.palette.divider}`,
    "&:hover": {
        borderColor: theme.palette.primary.main,
        transform: "translateY(-4px)",
        boxShadow: theme.shadows[12],
        "& .selection-indicator": {
            opacity: 1,
            transform: "scale(1.1)",
        },
    },
    "&.selected": {
        borderColor: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary.main, 0.05),
        boxShadow: theme.shadows[8],
    },
}))

const CopyShiftPlanModal: FC<ModalProps> = ({ open, onClose, departmentId, seasonId, isDepchief, employeeIds, fetchData }) => {
    const { t } = useTranslation()
    const [isLoading, setIsLoading] = useState(false)
    const [showSelection, setShowSelection] = useState(true)
    const [selectedMode, setSelectedMode] = useState<"copy" | "repeat" | null>(null)
    const [showConfirmation, setShowConfirmation] = useState(false)

    // Whether calendar events (days off, vacation, ill, ...) of the visible employees
    // should also be copied, and whether to replace existing ones in the target period.
    const [copyCalendarEntries, setCopyCalendarEntries] = useState(false)
    const [replaceCalendarEntries, setReplaceCalendarEntries] = useState(false)
    const canCopyCalendarEntries = !!employeeIds && employeeIds.length > 0

    // Reset when modal opens
    useEffect(() => {
        if (open) {
            setShowSelection(true)
            setSelectedMode(null)
            setCopyCalendarEntries(false)
            setReplaceCalendarEntries(false)
        }
    }, [open])
    const [pendingAction, setPendingAction] = useState<{
        type: "quick" | "custom" | "repeat"
        dto: CopyShiftPlanDto | RepeatShiftPlanDto
    } | null>(null)
    
    const [copyFromDate, setCopyFromDate] = useState<Date>(startOfWeek(new Date(), { weekStartsOn: 1 }))
    const [copyToDate, setCopyToDate] = useState<Date>(endOfWeek(new Date(), { weekStartsOn: 1 }))
    const [pasteStartDate, setPasteStartDate] = useState<Date>(addDays(endOfWeek(new Date(), { weekStartsOn: 1 }), 1))
    const [quickActionWeekPasteDate, setQuickActionWeekPasteDate] = useState<Date>(addDays(endOfWeek(new Date(), { weekStartsOn: 1 }), 1))
    
    // Repeat pattern dates
    const [repeatCopyFromStartDate, setRepeatCopyFromStartDate] = useState<Date>(startOfWeek(new Date(), { weekStartsOn: 1 }))
    const [repeatCopyFromEndDate, setRepeatCopyFromEndDate] = useState<Date>(endOfWeek(new Date(), { weekStartsOn: 1 }))
    const [repeatCopyToStartDate, setRepeatCopyToStartDate] = useState<Date>(addDays(endOfWeek(new Date(), { weekStartsOn: 1 }), 1))
    const [repeatCopyToEndDate, setRepeatCopyToEndDate] = useState<Date>(addDays(endOfWeek(new Date(), { weekStartsOn: 1 }), 8))
    const getDefaultMonthPasteDate = () => {
        const currentMonthStart = startOfMonth(new Date())
        const currentMonthStartWeekday = getDay(currentMonthStart)
        const nextMonthStart = startOfMonth(addDays(endOfMonth(new Date()), 1))
        
        // Find the first day of next month that matches the weekday of current month start
        let targetDate = nextMonthStart
        while (getDay(targetDate) !== currentMonthStartWeekday) {
            targetDate = addDays(targetDate, 1)
        }
        
        return targetDate
    }

    const [quickActionMonthPasteDate, setQuickActionMonthPasteDate] = useState<Date>(getDefaultMonthPasteDate())

    const showWeekdayWarning = () => {
        if (!copyFromDate || !pasteStartDate) return false
        return getDay(copyFromDate) !== getDay(pasteStartDate)
    }

    const showQuickActionWeekdayWarning = (pasteDate: Date) => {
        const currentWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 })
        return getDay(currentWeekStart) !== getDay(pasteDate)
    }

    const showMonthQuickActionWeekdayWarning = (pasteDate: Date) => {
        const currentMonthStart = startOfMonth(new Date())
        return getDay(currentMonthStart) !== getDay(pasteDate)
    }



    // Helper function to format date at local midnight to avoid timezone issues
    const formatDateForBackend = (date: Date, isEndDate: boolean = false): string => {
        const localDate = new Date(date)
        localDate.setHours(0, 0, 0, 0)
        const dateString = format(localDate, 'yyyy-MM-dd')
        // For end date, use end of day to include the entire day
        return isEndDate ? `${dateString}T23:59:59.999Z` : `${dateString}T00:00:00.000Z`
    }

    // Fields shared by every copy/repeat DTO. The real season id is always sent so the
    // backend can scope the calendar events; the calendar flags only take effect when
    // there are employees visible on the page to copy them for.
    const calendarDtoFields = {
        seasonId: seasonId ?? 0,
        copyCalendarEntries: canCopyCalendarEntries && copyCalendarEntries,
        replaceCalendarEntries: canCopyCalendarEntries && copyCalendarEntries && replaceCalendarEntries,
        employeeIds: employeeIds ?? [],
    }

    const handleCustomCopy = () => {
        if (!copyFromDate || !copyToDate || !pasteStartDate) {
            toast.error(t("shift.fillAllFields"))
            return
        }

        if (copyFromDate > copyToDate) {
            toast.error(t("shift.invalidDateRange"))
            return
        }

        const dto: CopyShiftPlanDto = {
            departmentId,
            ...calendarDtoFields,
            copyFromDate: formatDateForBackend(copyFromDate, false),
            copyToDate: formatDateForBackend(copyToDate, true),
            pasteStartDate: formatDateForBackend(pasteStartDate, false),
        }

        setPendingAction({ type: "custom", dto })
        setShowConfirmation(true)
    }

    const handleConfirmCopy = async () => {
        if (!pendingAction) return

        setIsLoading(true)
        try {
            if (pendingAction.type === "repeat") {
                await shiftApi.repeatShiftPlan(pendingAction.dto as RepeatShiftPlanDto)
                toast.success(t("shift.repeatSuccess"))
            } else {
                await shiftApi.copyShiftPlan(pendingAction.dto as CopyShiftPlanDto)
                toast.success(t("shift.copySuccess"))
            }
            fetchData()
            setShowConfirmation(false)
            setPendingAction(null)
            onClose()
        } catch (error) {
            if (pendingAction.type === "repeat") {
                toast.error(t("shift.repeatError"))
            } else {
                toast.error(t("shift.copyError"))
            }
        } finally {
            setIsLoading(false)
        }
    }

    const handleRepeatPattern = () => {
        if (!repeatCopyFromStartDate || !repeatCopyFromEndDate || !repeatCopyToStartDate || !repeatCopyToEndDate) {
            toast.error(t("shift.fillAllFields"))
            return
        }

        if (repeatCopyFromStartDate > repeatCopyFromEndDate) {
            toast.error(t("shift.invalidDateRange"))
            return
        }

        if (repeatCopyToStartDate > repeatCopyToEndDate) {
            toast.error(t("shift.invalidDateRange"))
            return
        }

        // Check if target period overlaps with source period
        // Two periods overlap if: targetStart <= sourceEnd AND targetEnd >= sourceStart
        // They don't overlap if: targetStart > sourceEnd OR targetEnd < sourceStart
        const sourceStart = startOfDay(repeatCopyFromStartDate)
        const sourceEnd = endOfDay(repeatCopyFromEndDate)
        const targetStart = startOfDay(repeatCopyToStartDate)
        const targetEnd = endOfDay(repeatCopyToEndDate)
        
        const overlaps = targetStart <= sourceEnd && targetEnd >= sourceStart
        if (overlaps) {
            toast.error(t("shift.overlapError"))
            return
        }

        const dto: RepeatShiftPlanDto = {
            departmentId,
            ...calendarDtoFields,
            copyFromStartDate: formatDateForBackend(repeatCopyFromStartDate, false),
            copyFromEndDate: formatDateForBackend(repeatCopyFromEndDate, true),
            copyToStartDate: formatDateForBackend(repeatCopyToStartDate, false),
            copyToEndDate: formatDateForBackend(repeatCopyToEndDate, true),
        }

        setPendingAction({ type: "repeat", dto })
        setShowConfirmation(true)
    }

    const handleCancelConfirmation = () => {
        setShowConfirmation(false)
        setPendingAction(null)
    }

    const handleModeSelection = (mode: "copy" | "repeat") => {
        setSelectedMode(mode)
        setShowSelection(false)
    }

    const handleBackToSelection = () => {
        setShowSelection(true)
        setSelectedMode(null)
    }

    // Visual example colors
    const colors = {
        day1: "#4CAF50",
        day2: "#2196F3",
        day3: "#FF9800",
        day4: "#9C27B0",
        day5: "#F44336",
    }

    // Selection Modal
    if (showSelection) {
        return (
            <StyledAppModal open={open} handleClose={onClose}>
                <Box sx={{ maxHeight: "85vh", overflow: "auto", p: 2 }}>
                    <FlexBox justifyContent="space-between" alignItems="center" mb={2}>
                        <FlexBox alignItems="center" gap={1.5}>
                            <H2 sx={{ fontSize: "1.75rem" }}>{t("shift.copyShiftPlan")}</H2>
                            <BetaChip label="BETA" size="small" />
                        </FlexBox>
                        <IconButton onClick={onClose} size="small">
                            <ContentCopy />
                        </IconButton>
                    </FlexBox>

                    <Typography variant="body1" color="text.secondary" mb={3} sx={{ fontSize: "0.95rem" }}>
                        {t("shift.selectModeDescription")}
                    </Typography>

                    <Grid container spacing={2}>
                        {/* Copy Mode */}
                        <Grid item xs={12} md={6}>
                            <SelectionCard
                                className={selectedMode === "copy" ? "selected" : ""}
                                onClick={() => handleModeSelection("copy")}
                            >
                                <CardContent sx={{ p: 2.5 }}>
                                    <FlexBox alignItems="center" gap={1.5} mb={2}>
                                        <ContentCopy color="primary" sx={{ fontSize: "2rem" }} />
                                        <Box>
                                            <H4 sx={{ fontSize: "1.2rem", mb: 0.5 }}>{t("shift.copyMode")}</H4>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.85rem" }}>
                                                {t("shift.copyModeDescription")}
                                            </Typography>
                                        </Box>
                                    </FlexBox>

                                    <Box mb={2}>
                                        <Typography variant="body2" fontWeight="bold" mb={1.5} sx={{ fontSize: "0.85rem" }}>
                                            {t("shift.example")}:
                                        </Typography>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" mb={0.75} sx={{ fontSize: "0.7rem", display: "block", fontWeight: 500 }}>
                                                {t("shift.sourcePeriod")} (3 {t("shift.days")}):
                                            </Typography>
                                            <FlexBox gap={0.75} mb={2} sx={{ flexWrap: "wrap" }}>
                                                {[1, 2, 3].map((day, idx) => (
                                                    <Box key={day} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                                                        <Typography variant="caption" sx={{ fontSize: "0.65rem", color: "text.secondary", fontWeight: 500 }}>
                                                            {String(day).padStart(2, '0')}.{String(day).padStart(2, '0')}
                                                        </Typography>
                                                        <CalendarDay color={[colors.day1, colors.day2, colors.day3][idx]}>
                                                            <span className="day-label">{String.fromCharCode(65 + idx)}</span>
                                                        </CalendarDay>
                                                    </Box>
                                                ))}
                                            </FlexBox>
                                            <ArrowForward sx={{ fontSize: "1.5rem", color: "text.secondary", mb: 1.5, display: "block", mx: "auto" }} />
                                            <Typography variant="caption" color="text.secondary" mb={0.75} sx={{ fontSize: "0.7rem", display: "block", fontWeight: 500 }}>
                                                {t("shift.targetPeriod")} - {t("shift.pasteStartDate")} only (3 {t("shift.days")}):
                                            </Typography>
                                            <FlexBox gap={0.75} sx={{ flexWrap: "wrap" }}>
                                                {[8, 9, 10].map((day, idx) => (
                                                    <Box key={day} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                                                        <Typography variant="caption" sx={{ fontSize: "0.65rem", color: "text.secondary", fontWeight: 500 }}>
                                                            {String(day).padStart(2, '0')}.{String(day).padStart(2, '0')}
                                                        </Typography>
                                                        <CalendarDay color={[colors.day1, colors.day2, colors.day3][idx]}>
                                                            <span className="day-label">{String.fromCharCode(65 + idx)}</span>
                                                        </CalendarDay>
                                                    </Box>
                                                ))}
                                            </FlexBox>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </SelectionCard>
                        </Grid>

                        {/* Repeat Mode */}
                        <Grid item xs={12} md={6}>
                            <SelectionCard
                                className={selectedMode === "repeat" ? "selected" : ""}
                                onClick={() => handleModeSelection("repeat")}
                            >
                                <CardContent sx={{ p: 2.5 }}>
                                    <FlexBox alignItems="center" gap={1.5} mb={2}>
                                        <Schedule color="primary" sx={{ fontSize: "2rem" }} />
                                        <Box>
                                            <H4 sx={{ fontSize: "1.2rem", mb: 0.5 }}>{t("shift.repeatMode")}</H4>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.85rem" }}>
                                                {t("shift.repeatModeDescription")}
                                            </Typography>
                                            <Alert severity="info" sx={{ mt: 1, py: 0.5, fontSize: "0.75rem" }}>
                                                <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                                                    {t("shift.repeatModeSingleDayHint")}
                                                </Typography>
                                            </Alert>
                                        </Box>
                                    </FlexBox>

                                    <Box mb={2}>
                                        <Typography variant="body2" fontWeight="bold" mb={1.5} sx={{ fontSize: "0.85rem" }}>
                                            {t("shift.example")}:
                                        </Typography>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" mb={0.75} sx={{ fontSize: "0.7rem", display: "block", fontWeight: 500 }}>
                                                {t("shift.patternPeriod")} (3 {t("shift.days")}):
                                            </Typography>
                                            <FlexBox gap={0.75} mb={2} sx={{ flexWrap: "wrap" }}>
                                                {[1, 2, 3].map((day, idx) => (
                                                    <Box key={day} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                                                        <Typography variant="caption" sx={{ fontSize: "0.65rem", color: "text.secondary", fontWeight: 500 }}>
                                                            {String(day).padStart(2, '0')}.{String(day).padStart(2, '0')}
                                                        </Typography>
                                                        <CalendarDay color={[colors.day1, colors.day2, colors.day3][idx]}>
                                                            <span className="day-label">{String.fromCharCode(65 + idx)}</span>
                                                        </CalendarDay>
                                                    </Box>
                                                ))}
                                            </FlexBox>
                                            <ArrowForward sx={{ fontSize: "1.5rem", color: "text.secondary", mb: 1.5, display: "block", mx: "auto" }} />
                                            <Typography variant="caption" color="text.secondary" mb={0.75} sx={{ fontSize: "0.7rem", display: "block", fontWeight: 500 }}>
                                                {t("shift.targetPeriod")} - {t("shift.copyToStartDate")} & {t("shift.copyToEndDate")} (5 {t("shift.days")}) - {t("shift.repeated")}:
                                            </Typography>
                                            <FlexBox gap={0.75} sx={{ flexWrap: "wrap" }}>
                                                {[8, 9, 10, 11, 12].map((day, idx) => {
                                                    const patternIdx = idx % 3;
                                                    return (
                                                        <Box key={day} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                                                            <Typography variant="caption" sx={{ fontSize: "0.65rem", color: "text.secondary", fontWeight: 500 }}>
                                                                {String(day).padStart(2, '0')}.{String(day).padStart(2, '0')}
                                                            </Typography>
                                                            <CalendarDay color={[colors.day1, colors.day2, colors.day3][patternIdx]}>
                                                                <span className="day-label">{String.fromCharCode(65 + patternIdx)}</span>
                                                            </CalendarDay>
                                                        </Box>
                                                    );
                                                })}
                                            </FlexBox>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </SelectionCard>
                        </Grid>
                    </Grid>

                    <FlexBox justifyContent="flex-end" gap={1.5} mt={3}>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            size="small"
                            sx={{ fontSize: "0.85rem", py: 0.75 }}
                        >
                            {t("common.forms.button.cancel")}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                if (selectedMode) {
                                    setShowSelection(false)
                                } else {
                                    toast.error(t("shift.selectModeFirst"))
                                }
                            }}
                            disabled={!selectedMode}
                            size="small"
                            sx={{ fontSize: "0.85rem", py: 0.75 }}
                        >
                            {t("common.continue")}
                        </Button>
                    </FlexBox>
                </Box>
            </StyledAppModal>
        )
    }

    // Main Modal (Copy or Repeat)
    return (
        <StyledAppModal open={open} handleClose={onClose}>
            <Box sx={{ maxHeight: "85vh", overflow: "auto", p: 1 }}>
                <FlexBox justifyContent="space-between" alignItems="center" mb={1.5}>
                    <FlexBox alignItems="center" gap={1.5}>
                        <IconButton onClick={handleBackToSelection} size="small" sx={{ mr: -1 }}>
                            <ArrowForward sx={{ transform: "rotate(180deg)" }} />
                        </IconButton>
                        <H2 sx={{ fontSize: "1.5rem" }}>
                            {selectedMode === "copy" ? t("shift.copyShiftPlan") : t("shift.repeatPattern")}
                        </H2>
                        <BetaChip label="BETA" size="small" />
                    </FlexBox>
                    <IconButton onClick={onClose} size="small">
                        <ContentCopy />
                    </IconButton>
                </FlexBox>

                <Alert severity="warning" sx={{ mb: 1.5, py: 1 }}>
                    <FlexBox alignItems="center" gap={1}>
                        <Warning color="warning" sx={{ fontSize: "1.1rem" }} />
                        <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                            {t("shift.copyWarning")}
                        </Typography>
                    </FlexBox>
                </Alert>

                {selectedMode === "copy" && (
                    <Grid container spacing={1.5} mb={1.5}>
                        <Grid item xs={12}>
                            <H4 mb={1} sx={{ fontSize: "1.1rem" }}>{t("shift.quickActions")}</H4>
                            <Grid container spacing={1}>
                            <Grid item xs={12} md={6}>
                                <QuickActionCard>
                                    <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                                        <FlexBox alignItems="center" gap={1.5} mb={1.5}>
                                            <CalendarMonth color="primary" sx={{ fontSize: "1.2rem" }} />
                                            <Box>
                                                <H6 sx={{ fontSize: "0.95rem", mb: 0.5 }}>{t("shift.copyCurrentWeek")}</H6>
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                                                    {format(startOfWeek(new Date(), { weekStartsOn: 1 }), "dd/MM/yyyy")} - {format(endOfWeek(new Date(), { weekStartsOn: 1 }), "dd/MM/yyyy")} · {t("shift.weekShort", { n: getISOWeek(new Date()) })}
                                                </Typography>
                                            </Box>
                                        </FlexBox>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" mb={0.5} sx={{ fontSize: "0.8rem" }}>
                                                {t("shift.pasteStartDate")}:
                                            </Typography>
                                            <CalendarInput
                                                value={quickActionWeekPasteDate}
                                                onChange={(newValue) => {
                                                    if (newValue) {
                                                        setQuickActionWeekPasteDate(newValue)
                                                    }
                                                }}
                                                format="dd/MM/yyyy"
                                                localeText={{
                                                    fieldDayPlaceholder: () => t("DD"),
                                                    fieldMonthPlaceholder: () => t("MM"),
                                                    fieldYearPlaceholder: () => t("YYYY")
                                                }}
                                            />
                                            {showQuickActionWeekdayWarning(quickActionWeekPasteDate) && (
                                                <Alert severity="warning" sx={{ mt: 0.75, py: 0.5 }}>
                                                    <FlexBox alignItems="center" gap={0.5}>
                                                        <Warning color="warning" sx={{ fontSize: "1rem" }} />
                                                        <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                                                            {t("shift.weekdayMismatchWarning")}
                                                        </Typography>
                                                    </FlexBox>
                                                </Alert>
                                            )}
                                            <Button
                                                variant="contained"
                                                size="small"
                                                fullWidth
                                                sx={{ mt: 1, fontSize: "0.8rem", py: 0.75 }}
                                                onClick={() => {
                                                    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 })
                                                    const weekEnd = endOfWeek(new Date(), { weekStartsOn: 1 })
                                                    const dto: CopyShiftPlanDto = {
                                                        departmentId,
                                                        ...calendarDtoFields,
                                                        copyFromDate: formatDateForBackend(weekStart, false),
                                                        copyToDate: formatDateForBackend(weekEnd, true),
                                                        pasteStartDate: formatDateForBackend(quickActionWeekPasteDate, false),
                                                    }
                                                    setPendingAction({ type: "quick", dto })
                                                    setShowConfirmation(true)
                                                }}
                                            >
                                                {t("shift.confirmCopy")}
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </QuickActionCard>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <QuickActionCard>
                                    <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                                        <FlexBox alignItems="center" gap={1.5} mb={1.5}>
                                            <Schedule color="primary" sx={{ fontSize: "1.2rem" }} />
                                            <Box>
                                                <H6 sx={{ fontSize: "0.95rem", mb: 0.5 }}>{t("shift.copyCurrentMonth")}</H6>
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                                                    {format(startOfMonth(new Date()), "dd/MM/yyyy")} - {format(endOfMonth(new Date()), "dd/MM/yyyy")}
                                                </Typography>
                                            </Box>
                                        </FlexBox>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" mb={0.5} sx={{ fontSize: "0.8rem" }}>
                                                {t("shift.pasteStartDate")}:
                                            </Typography>
                                            <CalendarInput
                                                value={quickActionMonthPasteDate}
                                                onChange={(newValue) => {
                                                    if (newValue) {
                                                        setQuickActionMonthPasteDate(newValue)
                                                    }
                                                }}
                                                format="dd/MM/yyyy"
                                                localeText={{
                                                    fieldDayPlaceholder: () => t("DD"),
                                                    fieldMonthPlaceholder: () => t("MM"),
                                                    fieldYearPlaceholder: () => t("YYYY")
                                                }}
                                            />
                                            {showMonthQuickActionWeekdayWarning(quickActionMonthPasteDate) && (
                                                <Alert severity="warning" sx={{ mt: 0.75, py: 0.5 }}>
                                                    <FlexBox alignItems="center" gap={0.5}>
                                                        <Warning color="warning" sx={{ fontSize: "1rem" }} />
                                                        <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                                                            {t("shift.weekdayMismatchWarning")}
                                                        </Typography>
                                                    </FlexBox>
                                                </Alert>
                                            )}
                                            <Button
                                                variant="contained"
                                                size="small"
                                                fullWidth
                                                sx={{ mt: 1, fontSize: "0.8rem", py: 0.75 }}
                                                onClick={() => {
                                                    const monthStart = startOfMonth(new Date())
                                                    const monthEnd = endOfMonth(new Date())
                                                    const dto: CopyShiftPlanDto = {
                                                        departmentId,
                                                        ...calendarDtoFields,
                                                        copyFromDate: formatDateForBackend(monthStart, false),
                                                        copyToDate: formatDateForBackend(monthEnd, true),
                                                        pasteStartDate: formatDateForBackend(quickActionMonthPasteDate, false),
                                                    }
                                                    setPendingAction({ type: "quick", dto })
                                                    setShowConfirmation(true)
                                                }}
                                            >
                                                {t("shift.confirmCopy")}
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </QuickActionCard>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                )}

                {selectedMode === "copy" && (
                    <>
                        <Divider sx={{ my: 1.5 }} />
                        <Box mb={1.5}>
                            <H4 mb={1} sx={{ fontSize: "1.1rem" }}>{t("shift.customCopy")}</H4>
                            <Grid container spacing={1.5}>
                        <Grid item xs={12} md={4}>
                            <CalendarInput
                                label={t("shift.copyFromDate")}
                                value={copyFromDate}
                                onChange={(newValue) => {
                                    if (newValue) setCopyFromDate(newValue)
                                }}
                                format="dd/MM/yyyy"
                                localeText={{
                                    fieldDayPlaceholder: () => t("DD"),
                                    fieldMonthPlaceholder: () => t("MM"),
                                    fieldYearPlaceholder: () => t("YYYY")
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <CalendarInput
                                label={t("shift.copyToDate")}
                                value={copyToDate}
                                onChange={(newValue) => {
                                    if (newValue) setCopyToDate(newValue)
                                }}
                                format="dd/MM/yyyy"
                                localeText={{
                                    fieldDayPlaceholder: () => t("DD"),
                                    fieldMonthPlaceholder: () => t("MM"),
                                    fieldYearPlaceholder: () => t("YYYY")
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <CalendarInput
                                label={t("shift.pasteStartDate")}
                                value={pasteStartDate}
                                onChange={(newValue) => {
                                    if (newValue) setPasteStartDate(newValue)
                                }}
                                format="dd/MM/yyyy"
                                localeText={{
                                    fieldDayPlaceholder: () => t("DD"),
                                    fieldMonthPlaceholder: () => t("MM"),
                                    fieldYearPlaceholder: () => t("YYYY")
                                }}
                            />
                        </Grid>
                    </Grid>
                    
                            {showWeekdayWarning() && (
                                <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                                    <FlexBox alignItems="center" gap={0.5}>
                                        <Warning color="warning" sx={{ fontSize: "1rem" }} />
                                        <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                                            {t("shift.weekdayMismatchWarning")}
                                        </Typography>
                                    </FlexBox>
                                </Alert>
                            )}
                        </Box>
                    </>
                )}

                {selectedMode === "repeat" && (
                    <>
                        <Divider sx={{ my: 1.5 }} />
                        <Box mb={1.5}>
                            <H4 mb={0.75} sx={{ fontSize: "1.1rem" }}>{t("shift.repeatPattern")}</H4>
                            <Typography variant="body2" color="text.secondary" mb={1} sx={{ fontSize: "0.8rem" }}>
                                {t("shift.repeatPatternDescription")}
                            </Typography>
                            <Grid container spacing={1.5}>
                                <Grid item xs={12} sm={6}>
                                    <CalendarInput
                                        label={t("shift.copyFromStartDate")}
                                        value={repeatCopyFromStartDate}
                                        onChange={(newValue) => {
                                            if (newValue) setRepeatCopyFromStartDate(newValue)
                                        }}
                                        format="dd/MM/yyyy"
                                        localeText={{
                                            fieldDayPlaceholder: () => t("DD"),
                                            fieldMonthPlaceholder: () => t("MM"),
                                            fieldYearPlaceholder: () => t("YYYY")
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <CalendarInput
                                        label={t("shift.copyFromEndDate")}
                                        value={repeatCopyFromEndDate}
                                        onChange={(newValue) => {
                                            if (newValue) setRepeatCopyFromEndDate(newValue)
                                        }}
                                        format="dd/MM/yyyy"
                                        localeText={{
                                            fieldDayPlaceholder: () => t("DD"),
                                            fieldMonthPlaceholder: () => t("MM"),
                                            fieldYearPlaceholder: () => t("YYYY")
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <CalendarInput
                                        label={t("shift.copyToStartDate")}
                                        value={repeatCopyToStartDate}
                                        onChange={(newValue) => {
                                            if (newValue) setRepeatCopyToStartDate(newValue)
                                        }}
                                        format="dd/MM/yyyy"
                                        localeText={{
                                            fieldDayPlaceholder: () => t("DD"),
                                            fieldMonthPlaceholder: () => t("MM"),
                                            fieldYearPlaceholder: () => t("YYYY")
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <CalendarInput
                                        label={t("shift.copyToEndDate")}
                                        value={repeatCopyToEndDate}
                                        onChange={(newValue) => {
                                            if (newValue) setRepeatCopyToEndDate(newValue)
                                        }}
                                        format="dd/MM/yyyy"
                                        localeText={{
                                            fieldDayPlaceholder: () => t("DD"),
                                            fieldMonthPlaceholder: () => t("MM"),
                                            fieldYearPlaceholder: () => t("YYYY")
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </>
                )}

                {canCopyCalendarEntries && (
                    <>
                        <Divider sx={{ my: 1.5 }} />
                        <Box mb={1.5}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={copyCalendarEntries}
                                        onChange={(e) => setCopyCalendarEntries(e.target.checked)}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.9rem" }}>
                                            {t("shift.copyCalendarEntries")}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                                            {t("shift.copyCalendarEntriesHint")}
                                        </Typography>
                                    </Box>
                                }
                            />
                            {copyCalendarEntries && (
                                <FormControl sx={{ mt: 1, ml: 1 }}>
                                    <RadioGroup
                                        value={replaceCalendarEntries ? "replace" : "add"}
                                        onChange={(e) => setReplaceCalendarEntries(e.target.value === "replace")}
                                    >
                                        <FormControlLabel
                                            value="add"
                                            control={<Radio size="small" />}
                                            label={
                                                <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                                                    {t("shift.calendarEntriesAdd")}
                                                </Typography>
                                            }
                                        />
                                        <FormControlLabel
                                            value="replace"
                                            control={<Radio size="small" color="error" />}
                                            label={
                                                <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                                                    {t("shift.calendarEntriesReplace")}
                                                </Typography>
                                            }
                                        />
                                    </RadioGroup>
                                    {replaceCalendarEntries && (
                                        <Alert severity="warning" sx={{ mt: 0.5, py: 0.5 }}>
                                            <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                                                {t("shift.calendarEntriesReplaceWarning")}
                                            </Typography>
                                        </Alert>
                                    )}
                                </FormControl>
                            )}
                        </Box>
                    </>
                )}

                <FlexBox justifyContent="flex-end" gap={1.5} mt={1.5}>
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={isLoading}
                        size="small"
                        sx={{ fontSize: "0.85rem", py: 0.75 }}
                    >
                        {t("common.forms.button.cancel")}
                    </Button>
                    {selectedMode === "copy" && (
                        <Button
                            variant="contained"
                            onClick={handleCustomCopy}
                            disabled={isLoading}
                            size="small"
                            startIcon={<ContentCopy sx={{ fontSize: "1rem" }} />}
                            sx={{ fontSize: "0.85rem", py: 0.75 }}
                        >
                            {isLoading ? t("common.loading") : t("shift.copyShiftPlan")}
                        </Button>
                    )}
                    {selectedMode === "repeat" && (
                        <Button
                            variant="contained"
                            onClick={handleRepeatPattern}
                            disabled={isLoading}
                            size="small"
                            startIcon={<Schedule sx={{ fontSize: "1rem" }} />}
                            sx={{ fontSize: "0.85rem", py: 0.75 }}
                        >
                            {isLoading ? t("common.loading") : t("shift.repeatPattern")}
                        </Button>
                    )}
                </FlexBox>
            </Box>

            <AppModal open={showConfirmation} handleClose={handleCancelConfirmation}>
                <Box sx={{ maxHeight: "85vh", overflow: "auto" }}>
                    <FlexBox justifyContent="space-between" alignItems="center" mb={3}>
                        <FlexBox alignItems="center" gap={2}>
                            <H2>{t("shift.confirmCopy")}</H2>
                            <BetaChip label="BETA" size="small" />
                        </FlexBox>
                        <IconButton onClick={handleCancelConfirmation} size="small">
                            <DeleteForever color="error" />
                        </IconButton>
                    </FlexBox>

                    <Alert severity="error" sx={{ mb: 3 }}>
                        <FlexBox alignItems="center" gap={1}>
                            <DeleteForever color="error" />
                            <Typography variant="body2">
                                {t("shift.confirmCopyWarning")}
                            </Typography>
                        </FlexBox>
                    </Alert>

                    {pendingAction && (
                        <Box mb={3}>
                            {pendingAction.type === "repeat" ? (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" mb={1}>
                                                {t("shift.copyFromStartDate")}:
                                            </Typography>
                                            <Typography variant="body1" fontWeight="bold">
                                                {format(new Date((pendingAction.dto as RepeatShiftPlanDto).copyFromStartDate), "dd/MM/yyyy")}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" mb={1}>
                                                {t("shift.copyFromEndDate")}:
                                            </Typography>
                                            <Typography variant="body1" fontWeight="bold">
                                                {format(new Date((pendingAction.dto as RepeatShiftPlanDto).copyFromEndDate), "dd/MM/yyyy")}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" mb={1}>
                                                {t("shift.copyToStartDate")}:
                                            </Typography>
                                            <Typography variant="body1" fontWeight="bold">
                                                {format(new Date((pendingAction.dto as RepeatShiftPlanDto).copyToStartDate), "dd/MM/yyyy")}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" mb={1}>
                                                {t("shift.copyToEndDate")}:
                                            </Typography>
                                            <Typography variant="body1" fontWeight="bold">
                                                {format(new Date((pendingAction.dto as RepeatShiftPlanDto).copyToEndDate), "dd/MM/yyyy")}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            ) : (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" mb={1}>
                                                {t("shift.copyFromDate")}:
                                            </Typography>
                                            <Typography variant="body1" fontWeight="bold">
                                                {format(new Date((pendingAction.dto as CopyShiftPlanDto).copyFromDate), "dd/MM/yyyy")}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" mb={1}>
                                                {t("shift.copyToDate")}:
                                            </Typography>
                                            <Typography variant="body1" fontWeight="bold">
                                                {format(new Date((pendingAction.dto as CopyShiftPlanDto).copyToDate), "dd/MM/yyyy")}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" mb={1}>
                                                {t("shift.pasteStartDate")}:
                                            </Typography>
                                            <Typography variant="body1" fontWeight="bold">
                                                {format(new Date((pendingAction.dto as CopyShiftPlanDto).pasteStartDate), "dd/MM/yyyy")}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            )}
                        </Box>
                    )}

                    {pendingAction?.dto.copyCalendarEntries && (
                        <Alert severity="info" sx={{ mb: 3 }}>
                            <Typography variant="body2">
                                {pendingAction.dto.replaceCalendarEntries
                                    ? t("shift.confirmIncludesCalendarReplace")
                                    : t("shift.confirmIncludesCalendarAdd")}
                            </Typography>
                        </Alert>
                    )}

                    <Typography variant="body1" color="text.secondary" mb={3}>
                        {t("shift.confirmCopyDescription")}
                    </Typography>

                    <FlexBox justifyContent="flex-end" gap={2}>
                        <Button
                            variant="outlined"
                            onClick={handleCancelConfirmation}
                            disabled={isLoading}
                        >
                            {t("common.forms.button.cancel")}
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleConfirmCopy}
                            disabled={isLoading}
                            startIcon={<DeleteForever />}
                        >
                            {isLoading ? t("common.loading") : t("shift.confirmCopy")}
                        </Button>
                    </FlexBox>
                </Box>
            </AppModal>
        </StyledAppModal>
    )
}

export default CopyShiftPlanModal 